import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { sendMessageToParent, useParentMessage } from 'services/iframeMessaging/iframeMessaging';
import { buildTaskVariables } from 'services/tasks/tasks';
import { SUPPORT_EMAIL } from 'support/helpers/const/const';
import { RelationshipVariables } from 'support/helpers/relationships/relationships';
import { interpolateString } from 'support/helpers/strings/interpolateString';
import { DocumentType, InternalRoutes } from 'support/types';
import portalSchema from 'support/types/schema-portal';
import { useLocalFileUpload } from 'pages/TasksPreview/hooks/useLocalFileUpload';
import { ConnectionSetupTable, ConnectionSetupTableRow } from 'components/ConnectionSetupTable/ConnectionSetupTable';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { Page } from 'components/Page/Page';
import { TaskSlideOver } from 'components/TaskSlideOver/TaskSlideOver';
import { Translate } from 'components/Translate/Translate';

type TaskBase = portalSchema.components['schemas']['TaskBase'];

type TasksPreviewDirection = 'SOURCE' | 'DESTINATION';

type TasksPreviewPartner = { id: string; name: string; logoUrl: string | null };

type TasksPreviewData = {
  tasks: Array<TaskBase>;
  /** The counterparty named in the task copy. */
  tradePartner: TasksPreviewPartner;
  /** The partner filling in the tasks, whose perspective the copy is written from. */
  answeringPartner: TasksPreviewPartner;
  /** The answering partner's direction: SOURCE when it sends the message type. */
  direction: TasksPreviewDirection;
  messageType: DocumentType;
};

const TASKS_PREVIEW_MESSAGE_TYPE = 'TASKS_PREVIEW_DATA';
const TASK_RESPONSE_MESSAGE_TYPE = 'TASK_RESPONSE_DATA';

export const TasksPreview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { taskId } = useParams<{ taskId: string }>();

  const { uploadFile, getFileDownloadUrl } = useLocalFileUpload();
  const validate = useCallback(isTasksPreviewData, []);
  const postMessageData = useParentMessage<TasksPreviewData>({
    type: TASKS_PREVIEW_MESSAGE_TYPE,
    validate,
  });

  const onSubmit = useCallback(
    (canonicalData: Record<string, unknown>) => {
      sendMessageToParent(TASK_RESPONSE_MESSAGE_TYPE, { taskId, canonical: canonicalData });
    },
    [taskId],
  );

  const direction = postMessageData?.direction;
  const messageType = postMessageData?.messageType;

  const extractedVariables: RelationshipVariables | undefined = useMemo(
    () =>
      postMessageData
        ? {
            partnerName: postMessageData.tradePartner.name,
            messageType: postMessageData.messageType,
            direction: postMessageData.direction === 'SOURCE' ? 'OUTBOUND' : 'INBOUND',
          }
        : undefined,
    [postMessageData],
  );

  const variables = useMemo(() => buildTaskVariables(extractedVariables, t), [extractedVariables, t]);

  const tasks = postMessageData?.tasks;
  const selectedTask = tasks?.find((task) => task.id === taskId);

  const rows: Array<ConnectionSetupTableRow> | undefined = tasks?.map((task) => ({
    id: task.id,
    title: (
      <span className="flex items-center gap-2">
        {interpolateString(task.title, variables)}
        {task.status === 'DRAFT' && <Badge color={BadgeColor.yellow}>Draft</Badge>}
      </span>
    ),
    status: 'TODO',
    ownership: task.ownership === 'PROCUROS' ? 'procuros' : 'you',
    actionRequired: task.ownership !== 'PROCUROS',
    path: generatePath(InternalRoutes.tasksPreviewTask, { taskId: task.id }),
  }));

  if (!postMessageData) {
    return (
      <Page isLoading isInternal>
        {null}
      </Page>
    );
  }

  return (
    <Page isInternal>
      <div className="mx-auto max-w-4xl space-y-6 p-8">
        <div className="space-y-4">
          <h1 className="text-2xl font-semibold">Tasks preview</h1>
          <p className="text-sm text-gray-500">
            As answered by{' '}
            <span className="font-medium text-gray-700">{postMessageData.answeringPartner.name || '-'}</span>, who{' '}
            {direction === 'SOURCE' ? 'sends' : 'receives'} {messageType || '-'}{' '}
            {direction === 'SOURCE' ? 'to' : 'from'}{' '}
            <span className="font-medium text-gray-700">{postMessageData.tradePartner.name || '-'}</span>
          </p>
        </div>

        {rows && rows.length > 0 ? (
          <section>
            <ConnectionSetupTable rows={rows} />
            <p className="pt-4 text-xs leading-4 text-gray-500">
              <Translate
                i18nKey="connections:relationshipDetails.setupTable.support"
                components={{ link: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-900 underline" /> }}
              />
            </p>
          </section>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
            No tasks were found for the provided filters.
          </div>
        )}
      </div>
      <TaskSlideOver
        open={Boolean(taskId)}
        onClose={() => navigate(InternalRoutes.tasksPreview)}
        task={selectedTask}
        variables={variables}
        partnerName={postMessageData.tradePartner.name}
        partnerLogoUrl={postMessageData.tradePartner.logoUrl}
        onSubmit={onSubmit}
        uploadFile={uploadFile}
        getFileDownloadUrl={getFileDownloadUrl}
      />
    </Page>
  );
};

const isTasksPreviewData = (data: unknown): data is TasksPreviewData => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const d = data as Record<string, unknown>;

  return (
    Array.isArray(d.tasks) &&
    typeof d.tradePartner === 'object' &&
    d.tradePartner !== null &&
    typeof d.answeringPartner === 'object' &&
    d.answeringPartner !== null &&
    (d.direction === 'SOURCE' || d.direction === 'DESTINATION') &&
    typeof d.messageType === 'string'
  );
};
